import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Download, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Photo {
  id: string;
  url: string;
  thumbnailUrl: string;
  caption?: string;
  uploadedAt: string;
  uploadedBy: string;
}

interface PhotoGalleryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photos: Photo[];
  assetName: string;
}

export function PhotoGalleryModal({
  open,
  onOpenChange,
  photos,
  assetName,
}: Readonly<PhotoGalleryModalProps>) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageError, setImageError] = useState(false);

  const currentPhoto = photos[currentIndex];

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
    setImageError(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
    setImageError(false);
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    setImageError(false);
  };

  const handleDownload = () => {
    if (currentPhoto) {
      window.open(currentPhoto.url, '_blank');
    }
  };

  if (!currentPhoto) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center justify-between">
            <div>
              <div className="text-lg font-semibold">{assetName}</div>
              <div className="text-sm font-normal text-slate-600 mt-1">
                Photo {currentIndex + 1} of {photos.length}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                className="h-9 px-3"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Main Image */}
        <div className="flex-1 relative bg-slate-900 flex items-center justify-center overflow-hidden">
          {!imageError ? (
            <img
              src={currentPhoto.url}
              alt={currentPhoto.caption || `Photo ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-slate-400">
              <Camera className="h-16 w-16 mb-4" />
              <p className="text-lg">Failed to load image</p>
            </div>
          )}

          {/* Navigation Arrows */}
          {photos.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/50 hover:bg-black/70 text-white"
                onClick={handlePrevious}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/50 hover:bg-black/70 text-white"
                onClick={handleNext}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
        </div>

        {/* Photo Info */}
        {currentPhoto.caption && (
          <div className="px-6 py-3 border-t bg-slate-50">
            <p className="text-sm text-slate-700">{currentPhoto.caption}</p>
            <p className="text-xs text-slate-500 mt-1">
              Uploaded by {currentPhoto.uploadedBy} on{' '}
              {new Date(currentPhoto.uploadedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
          </div>
        )}

        {/* Thumbnail Strip */}
        {photos.length > 1 && (
          <div className="px-6 py-4 border-t bg-white">
            <div className="flex gap-2 overflow-x-auto">
              {photos.map((photo, index) => (
                <button
                  key={photo.id}
                  onClick={() => handleThumbnailClick(index)}
                  className={cn(
                    'relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all',
                    index === currentIndex
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  <img
                    src={photo.thumbnailUrl}
                    alt={photo.caption || `Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {index === currentIndex && (
                    <div className="absolute inset-0 bg-blue-500/10" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
